import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { Modal, Select } from 'antd';
import debug from 'debug';
import { Icons } from 'icons';
import { cloneDeep } from 'lodash-es';

import { defaultFilterOptions } from 'components/common/utils';
import { postCheckTransitions } from 'lib/apis/workflow';
import { classnames } from 'lib/classnames';
import fetch from 'lib/fetch';
import useI18n from 'lib/hooks/useI18n';
import { FieldConfig } from 'lib/types/field';
import { ResultType, WorkflowDataProps } from 'lib/types/workflow';

import ChangeField from '../../../item-move/changeField';
import Status from '../../../item-move/Status';

import styles from './index.module.less';

const cx = classnames(styles);
const Viewer = React.lazy(() => import(/* webpackPreload: true */ 'components/workflow/View'));

export type SelectTransitionType = {
  name: string;
  source: {
    key: string;
    name: string;
    type?: string;
  };
  target: {
    key: string;
    name: string;
    type?: string;
  };
};

interface ChangeOperatorProps {
  item: Parse.Attributes;
  itemWorkflow: WorkflowDataProps;
  moveItemType: string;
  workflowData: WorkflowDataProps;
  workflowIsChange: boolean;
  onChange?: (
    itemId: string,
    data: {
      transition: SelectTransitionType;
      values: Record<string, FieldConfig>;
      flowValues?: Record<string, FieldConfig>;
    },
    canNext: boolean,
  ) => void;
  updateRequireFields?: (itemId: string, fields: FieldConfig[], flowFields?: FieldConfig[]) => void;
  updateErrorMessage: (itemId: string, result: ResultType) => void;
  requireFields: any[];
  requireValue: Record<string, any>;
  loading: boolean;
}

export const DEFAULT_FLOW_MESSAGE: ResultType = { result: false, message: '' };
const FlowViewType = { current: 'current', moved: 'moved' };

const log = debug('changeData:content');

function getRequireFieldBehaviors(fieldBehaviors, values) {
  log('get require field behavior', fieldBehaviors, values);
  const keys = Object.keys(fieldBehaviors);
  const requireFields = keys
    .filter(key => {
      const isRequire = fieldBehaviors[key].behaviors.includes('required');
      const fieldValue = values[key];
      const hasValue = Array.isArray(fieldValue) ? fieldValue.length : fieldValue;
      return isRequire && !hasValue;
    })
    .map(key => fieldBehaviors[key].field);
  log('require fields', requireFields);
  return requireFields;
}

const StatusView = ({ id, nodes }) => {
  const { t } = useI18n();
  const node = nodes.find(node => node.id === id) || {};
  let name = node.name;
  if (node.type === 'Start') {
    name = t('pages.workflow.default.startNodeName');
  }
  return <Status name={name} type={node.key} />;
};

const ChangeOperator: React.FC<ChangeOperatorProps> = ({
  item,
  itemWorkflow,
  moveItemType,
  workflowIsChange,
  workflowData,
  onChange,
  updateRequireFields,
  updateErrorMessage,
  requireFields,
  requireValue,
  loading,
}) => {
  const { t } = useI18n();
  const [selectTransition, setSelectTransition] = useState();
  const [selectTransitionData, setSelectTransitionData] = useState<SelectTransitionType>(undefined);
  const [flowMessage, setFlowMessage] = useState(DEFAULT_FLOW_MESSAGE);
  const [flowRequireFields, setFlowRequireFields] = useState([]);
  const [flowRequireValue, setFlowRequireValue] = useState({});
  const [flowVisible, setFlowVisible] = useState<boolean>(undefined);
  const [flowViewer, setFlowViewer] = useState<string>('');

  const formFlag = useMemo(
    () => requireFields.every(field => !!requireValue[field.key]),
    [requireFields, requireValue],
  );
  const workflowFlag = useMemo(
    () => !workflowIsChange || (flowRequireFields.every(field => !!flowRequireValue[field.key]) && flowMessage.result),
    [flowMessage.result, flowRequireFields, flowRequireValue, workflowIsChange],
  );

  const fieldChange = useCallback(
    (value, key) => {
      log('field change', value, key);
      requireValue[key] = value;
      onChange(
        item?.objectId,
        { transition: selectTransitionData, values: requireValue, flowValues: flowRequireValue },
        requireFields.every(field => !!requireValue[field.key]) && workflowFlag,
      );
    },
    [requireValue, onChange, item?.objectId, selectTransitionData, flowRequireValue, requireFields, workflowFlag],
  );

  const flowFieldChange = useCallback(
    (value, key) => {
      log('flow field change', value, key);
      flowRequireValue[key] = value;
      setFlowRequireValue(cloneDeep(flowRequireValue));
      onChange(
        item?.objectId,
        { transition: selectTransitionData, values: requireValue, flowValues: flowRequireValue },
        formFlag && flowRequireFields.every(field => !!flowRequireValue[field.key] && flowMessage.result),
      );
    },
    [
      flowRequireValue,
      onChange,
      item?.objectId,
      selectTransitionData,
      requireValue,
      formFlag,
      flowRequireFields,
      flowMessage.result,
    ],
  );

  const flowChange = useCallback(
    async (value, { transition, nodes }) => {
      log('transition change', value, transition, nodes);

      const {
        name: transitionName,
        source: { key: sourceKey, name: sourceName },
        target: { key: targetKey, name: targetName },
      } = transition;
      const sourceSelectNode = nodes.find(node => node.statusId === sourceKey);
      const targetSelectNode = nodes.find(node => node.statusId === targetKey);
      const transitionData = {
        name: transitionName,
        source: { key: sourceKey, name: sourceName, type: sourceSelectNode.key },
        target: { key: targetKey, name: targetName, type: targetSelectNode.key },
      };
      let transitionResult = { result: true };
      if (transition.parameters?.ruleCombination) {
        const checkResult = await postCheckTransitions([
          {
            itemId: item.objectId,
            workflowId: workflowData?.objectId,
            transitionId: transition.id,
          },
        ]);
        const { result } = checkResult[0];
        transitionResult = result;
        log('transition condition result', transitionResult);
        setFlowMessage(transitionResult);
        updateErrorMessage(item?.objectId, transitionResult);
      } else {
        setFlowMessage(transitionResult);
        updateErrorMessage(item?.objectId, transitionResult);
      }

      // 当有填写界面时的校验
      if (transition.parameters?.screen) {
        const fieldBehaviors = await fetch.$post(`/fieldBehavior/screen/${transition.parameters.screen.value}`, {
          workflow: workflowData?.objectId,
          itemType: moveItemType,
          targetStatus: targetSelectNode.statusId,
          transition: transitionName,
        });
        log('field behavior', fieldBehaviors);
        const fields = getRequireFieldBehaviors(fieldBehaviors, item.values || {});
        log('require fields', fields);
        const changeFields = fields.filter(
          field => !requireFields.find(requireField => requireField.key === field.key),
        );
        setFlowRequireFields(changeFields);
        setFlowRequireValue({});
        onChange(
          item?.objectId,
          { transition: transitionData, values: requireValue, flowValues: {} },
          !changeFields.length && formFlag && transitionResult.result,
        );
        updateRequireFields(item?.objectId, requireFields, changeFields);
      } else {
        setFlowRequireFields([]);
        setFlowRequireValue({});
        onChange(
          item?.objectId,
          { transition: transitionData, values: requireValue },
          formFlag && transitionResult.result,
        );
        updateRequireFields(item?.objectId, requireFields);
      }

      setSelectTransition(value);
      setSelectTransitionData(transitionData);
    },
    [
      item.objectId,
      item.values,
      workflowData?.objectId,
      updateErrorMessage,
      moveItemType,
      onChange,
      requireValue,
      formFlag,
      updateRequireFields,
      requireFields,
    ],
  );

  // 判断是否有字段改变或者工作流改变
  const hasChange = useMemo(() => {
    return !!requireFields?.length || workflowIsChange;
  }, [requireFields.length, workflowIsChange]);

  const fieldContent = useMemo(() => {
    return (
      <>
        {!!requireFields?.length && (
          <article className={cx('field-area')}>
            <div className={cx('change-title')}>{t('pages.itemMove.fieldChange')}</div>
            <div className={cx('field-list')}>
              {requireFields.map(field => (
                <ChangeField key={field.key} field={field} value={requireValue[field.key]} onChange={fieldChange} />
              ))}
            </div>
          </article>
        )}
        {workflowIsChange && (
          <article className={cx('flow-area')}>
            <div className={cx('change-title')}>
              {t('pages.itemMove.statusChange')}
              <p className={cx('title-tip')}>
                （{t('pages.itemMove.current.workflow')}
                <span
                  className="btn-active"
                  onClick={() => {
                    setFlowViewer(FlowViewType.current);
                    setFlowVisible(true);
                  }}
                >
                  {itemWorkflow?.name}
                </span>
                ｜{t('pages.itemMove.next.workflow')}
                <span
                  className="btn-active"
                  onClick={() => {
                    setFlowViewer(FlowViewType.moved);
                    setFlowVisible(true);
                  }}
                >
                  {workflowData?.name}
                </span>
                ）
              </p>
            </div>
            <div className={cx('flow-table')}>
              <div className={cx('flow-item', 'flow-header')}>
                <div className={cx('left')}>{t('pages.itemMove.current.status')}</div>
                <div className={cx('right')}>{t('pages.itemMove.next.status')}</div>
              </div>
              <div className={cx('flow-item', 'flow-content')}>
                <div className={cx('left')}>
                  {Status({ name: item?.status?.name, type: item?.status?.type })}
                  <Icons.WorkflowArrow className={cx('arrow-icon')} />
                </div>
                <div className={cx('right')}>
                  <Select
                    className={cx('flow-select')}
                    showSearch
                    filterOption={defaultFilterOptions}
                    placeholder={t('pages.itemMove.selectTransition')}
                    value={selectTransition}
                    onChange={flowChange}
                  >
                    {workflowData?.transitions.map(transition => {
                      return (
                        <Select.Option
                          key={transition.id}
                          value={transition.id}
                          transition={transition}
                          nodes={workflowData?.nodes}
                        >
                          <div style={{ fontSize: '12px' }}>
                            <StatusView id={transition.target?.id} nodes={workflowData?.nodes} />（{transition.name}：
                            <StatusView id={transition.source?.id} nodes={workflowData?.nodes} />
                            <Icons.WorkflowArrow
                              style={{ height: '20px', verticalAlign: 'middle', fontSize: '18px' }}
                            />
                            <StatusView id={transition.target?.id} nodes={workflowData?.nodes} />）
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
              </div>
            </div>
            {!!flowRequireFields.length && (
              <article className={cx('field-area')}>
                <div className={cx('change-title')}>{t('pages.itemMove.workflowChange')}</div>
                <div className={cx('field-list')}>
                  {flowRequireFields.map(field => (
                    <ChangeField
                      key={field.key}
                      field={field}
                      value={flowRequireValue[field.key]}
                      onChange={flowFieldChange}
                    />
                  ))}
                </div>
              </article>
            )}
          </article>
        )}
      </>
    );
  }, [
    requireFields,
    workflowIsChange,
    itemWorkflow?.name,
    workflowData?.name,
    workflowData?.transitions,
    workflowData?.nodes,
    item?.status?.name,
    item?.status?.type,
    selectTransition,
    flowChange,
    flowRequireFields,
    requireValue,
    fieldChange,
    flowRequireValue,
    flowFieldChange,
    t,
  ]);

  return (
    <div className={cx('field-wrap')}>
      {loading ? (
        <span className={cx('sub-text')}>{t('pages.itemMove.handling')}</span>
      ) : hasChange ? (
        fieldContent
      ) : (
        <span className={cx('sub-text')}>{t('pages.itemMove.nothingCheck')}</span>
      )}
      <Modal
        destroyOnClose
        maskClosable={false}
        title={`${t('pages.itemMove.flowChart')}(${
          flowViewer === FlowViewType.current ? itemWorkflow.name : workflowData?.name
        })`}
        open={flowVisible}
        onCancel={() => setFlowVisible(false)}
        footer={null}
        width="70%"
      >
        <Suspense fallback={null}>
          {flowViewer === FlowViewType.current && itemWorkflow?.nodes && <Viewer initialData={itemWorkflow} />}
          {flowViewer === FlowViewType.moved && workflowData?.nodes && <Viewer initialData={workflowData} />}
        </Suspense>
      </Modal>
    </div>
  );
};

export default ChangeOperator;
